import {
  DataGrid,
  FilterToolbar,
  formatCurrency,
  formatDate,
  StatusBadge,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  AdminRkTransactionExportRowRes,
  AdminRkTransactionRowRes,
  GetAdminRkTransactionExportsParams,
  GetAdminRkTransactionsParams,
  useGetAdminRkTransactionExports,
  useGetAdminRkTransactions
} from '@ijoin/hooks';
import { Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';

import { FC, useState } from 'react';

import { LinkButton } from './LinkButton';
import { RkTransactionExportActionsCell } from './RkTransactionExportActionsCell';
import { useAdvisoryOrganizationOptions } from './useAdvisoryOrganizationOptions';
import { usePlanOptions } from './usePlanOptions';

type Row = AdminRkTransactionExportRowRes | AdminRkTransactionRowRes;

const SORTABLE_FIELDS = ['createdAt', 'postDate', 'sentToCarrierDate'];

const formatDay = (value: unknown) =>
  value
    ? formatDate(value as string, { format: 'MM/DD/YYYY', useUtc: true })
    : '';

const formatDateTime = (value: unknown) =>
  value ? formatDate(value as string, { format: 'MM/DD/YYYY h:mm:ss A' }) : '';

const formatAmount = (value: unknown) =>
  value == null ? '' : formatCurrency(value as number);

export const RkTransactionsGrid: FC<{
  mode: 'exports' | 'transactions';
}> = props => {
  const [query, setQuery] = useState<GridQuery>();
  const [draftOrgId, setDraftOrgId] = useState<string>();

  const orgs = useAdvisoryOrganizationOptions();
  const plans = usePlanOptions(draftOrgId);

  const transactions = useGetAdminRkTransactions(
    query as GetAdminRkTransactionsParams,
    {
      query: {
        enabled: props.mode === 'transactions' && !!query,
        placeholderData: keepPreviousData
      }
    }
  );

  const transactionExports = useGetAdminRkTransactionExports(
    query as GetAdminRkTransactionExportsParams,
    {
      query: {
        enabled: props.mode === 'exports' && !!query,
        placeholderData: keepPreviousData
      }
    }
  );

  const rows = props.mode === 'exports' ? transactionExports : transactions;

  const onDraftChange = (values: Record<string, unknown>) =>
    setDraftOrgId((values.advisoryOrganizationId as string) || undefined);

  const filters: FilterDef[] = [
    {
      isLoading: orgs.isLoading,
      label: 'Advisory Organization',
      name: 'advisoryOrganizationId',
      options: orgs.data ?? [],
      type: 'select'
    },
    { ...plans, label: 'Plan', name: 'planId', type: 'autocomplete' },
    { label: 'External Plan ID', name: 'externalPlanId', type: 'text' },
    { label: 'First Name', name: 'firstName', type: 'text' },
    { label: 'Last Name', name: 'lastName', type: 'text' },
    {
      endName: 'endPostDate',
      label: 'Post Date',
      startName: 'startPostDate',
      type: 'dateRange'
    },
    ...(props.mode === 'exports'
      ? [
          {
            label: 'Processed',
            name: 'isProcessed',
            options: [
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' }
            ],
            type: 'select' as const
          }
        ]
      : [])
  ];

  const columns: GridColDef<Row>[] = [
    {
      field: 'advisoryOrganizationName',
      flex: 1,
      headerName: 'Advisory Organization',
      minWidth: 200,
      renderCell: params =>
        params.row.advisoryOrganizationId ? (
          <LinkButton
            data-testid={`advisoryOrganization-${params.row.id}`}
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId
            }}
            to='/organizations/$advisoryOrganizationId'
            variant='text'>
            {params.row.advisoryOrganizationName}
          </LinkButton>
        ) : null
    },
    {
      field: 'planName',
      flex: 1,
      headerName: 'Plan',
      minWidth: 220,
      renderCell: params =>
        params.row.advisoryOrganizationId && params.row.planId ? (
          <LinkButton
            data-testid={`plan-${params.row.id}`}
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId'
            variant='text'>
            {params.row.planName}
          </LinkButton>
        ) : (
          params.row.planName
        )
    },
    {
      field: 'externalPlanId',
      flex: 0.75,
      headerName: 'External Plan ID',
      minWidth: 150
    },
    {
      field: 'participant',
      flex: 1,
      headerName: 'Participant',
      minWidth: 200,
      renderCell: params =>
        params.row.advisoryOrganizationId &&
        params.row.planId &&
        params.row.planEnrollmentId ? (
          <LinkButton
            data-testid={`participant-${params.row.id}`}
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planEnrollmentId: params.row.planEnrollmentId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId/participants/$planEnrollmentId'
            variant='text'>
            {`${params.row.firstName} ${params.row.lastName}`}
          </LinkButton>
        ) : (
          `${params.row.firstName} ${params.row.lastName}`
        )
    },
    {
      field: 'socialSecurityNumber',
      flex: 0.75,
      headerName: 'SSN',
      minWidth: 120
    },
    {
      field: 'postDate',
      flex: 0.75,
      headerName: 'Post Date',
      minWidth: 120,
      valueFormatter: formatDay
    },
    ...(props.mode === 'exports'
      ? [
          {
            field: 'transId',
            flex: 0.75,
            headerName: 'Trans ID',
            minWidth: 110,
            type: 'number' as const
          },
          {
            field: 'buySellCode',
            flex: 0.5,
            headerName: 'Buy/Sell',
            minWidth: 90
          },
          {
            field: 'totalTransAmt',
            flex: 0.75,
            headerName: 'Amount',
            minWidth: 120,
            type: 'number' as const,
            valueFormatter: formatAmount
          },
          {
            align: 'center' as const,
            field: 'isProcessed',
            flex: 0.5,
            headerAlign: 'center' as const,
            headerName: 'Processed',
            minWidth: 110,
            renderCell: (params: { value?: unknown }) =>
              params.value ? (
                <StatusBadge severity='success'>Processed</StatusBadge>
              ) : (
                <StatusBadge severity='warning'>Pending</StatusBadge>
              )
          },
          {
            field: 'sentToCarrierDate',
            flex: 0.75,
            headerName: 'Sent To Carrier',
            minWidth: 170,
            valueFormatter: formatDateTime
          },
          {
            field: 'carrierTransactionCode',
            flex: 0.75,
            headerName: 'Carrier Transaction',
            minWidth: 160
          },
          {
            field: 'carrierResultMessage',
            flex: 1,
            headerName: 'Carrier Result',
            minWidth: 200
          },
          {
            field: 'tradeIdentifier',
            flex: 1,
            headerName: 'Trade Identifier',
            minWidth: 200
          },
          {
            field: 'fileIdentifier',
            flex: 1,
            headerName: 'File Identifier',
            minWidth: 200
          }
        ]
      : [
          {
            field: 'transType',
            flex: 1,
            headerName: 'Type',
            minWidth: 160,
            valueGetter: (value: unknown, row: Row) =>
              [(row as AdminRkTransactionRowRes).transTypeCode, value]
                .filter(Boolean)
                .join(' · ')
          },
          {
            field: 'transSubTypeCode',
            flex: 0.5,
            headerName: 'Sub Type',
            minWidth: 90
          },
          {
            field: 'buySellCode',
            flex: 0.5,
            headerName: 'Buy/Sell',
            minWidth: 90
          },
          {
            field: 'externalFundId',
            flex: 1,
            headerName: 'Fund',
            minWidth: 160
          },
          {
            field: 'contributionSourceId',
            flex: 0.5,
            headerName: 'Source',
            minWidth: 90
          },
          {
            field: 'numberOfUnits',
            flex: 0.75,
            headerName: 'Units',
            minWidth: 110,
            type: 'number' as const
          },
          {
            field: 'unitValue',
            flex: 0.75,
            headerName: 'Unit Value',
            minWidth: 110,
            type: 'number' as const,
            valueFormatter: formatAmount
          },
          {
            field: 'totalDollarAmt',
            flex: 0.75,
            headerName: 'Amount',
            minWidth: 120,
            type: 'number' as const,
            valueFormatter: formatAmount
          },
          {
            field: 'tradeDate',
            flex: 0.75,
            headerName: 'Trade Date',
            minWidth: 120,
            valueFormatter: formatDay
          },
          {
            field: 'statusCode',
            flex: 0.5,
            headerName: 'Status',
            minWidth: 80
          },
          {
            align: 'center' as const,
            field: 'reversalIndicator',
            flex: 0.5,
            headerAlign: 'center' as const,
            headerName: 'Reversal',
            minWidth: 100,
            renderCell: (params: { value?: unknown }) =>
              params.value ? (
                <StatusBadge severity='warning'>Reversal</StatusBadge>
              ) : null
          },
          {
            field: 'dataSourceType',
            flex: 0.5,
            headerName: 'Source Type',
            minWidth: 110
          },
          {
            field: 'transId',
            flex: 0.75,
            headerName: 'Trans ID',
            minWidth: 110,
            type: 'number' as const
          },
          {
            field: 'seqNum',
            flex: 0.5,
            headerName: 'Seq',
            minWidth: 70,
            type: 'number' as const
          }
        ]),
    {
      align: 'center',
      field: 'isRoth',
      flex: 0.5,
      headerAlign: 'center',
      headerName: 'Roth',
      minWidth: 90,
      renderCell: params =>
        params.value ? <StatusBadge severity='info'>Roth</StatusBadge> : null
    },
    {
      field: 'createdAt',
      flex: 0.75,
      headerName: 'Created',
      minWidth: 170,
      valueFormatter: formatDateTime
    },
    ...(props.mode === 'exports'
      ? [
          {
            align: 'center' as const,
            disableColumnMenu: true,
            display: 'flex' as const,
            field: 'actions',
            headerName: '',
            renderCell: (params: { row: Row }) => (
              <RkTransactionExportActionsCell
                transactionExport={params.row as AdminRkTransactionExportRowRes}
              />
            ),
            width: 67
          }
        ]
      : [])
  ];

  return (
    <Stack sx={{ flex: 1, minHeight: 0, width: '100%' }}>
      <DataGrid
        autoHeight={false}
        columns={columns.map(column => ({
          ...column,
          sortable: SORTABLE_FIELDS.includes(column.field)
        }))}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.id}
        initialSort={[{ field: 'postDate', sort: 'desc' }]}
        loading={!query || rows.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSizeOptions={[25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{
          left: ['advisoryOrganizationName', 'planName'],
          right: props.mode === 'exports' ? ['actions'] : []
        }}
        rowCount={rows.data?.pagination.total ?? 0}
        rows={rows.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid':
              props.mode === 'exports'
                ? 'rkTransactionExports'
                : 'rkTransactions'
          },
          toolbar: {
            onDraftChange,
            onRefresh: rows.refetch,
            searchButtonTestId: `${props.mode}Search`,
            triggerTestId: `${props.mode}FilterTrigger`
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

RkTransactionsGrid.displayName = 'RkTransactionsGrid';
